import { createSelector, MemoizedSelector, select } from '@ngrx/store';
import { map, pipe } from 'rxjs';

import { createDeepEqualSelector, safeSelect, StateSelector } from '@fcom/core/selectors/selector-utils';
import { Amount } from '@fcom/dapi/interfaces/dapi.interface';
import { CartStatus, NO_VARIANTS } from '@fcom/dapi/interfaces/cart.interface';
import {
  SelectedServiceData,
  SelectionsPerTraveler,
  ServiceAvailability,
  ServiceCatalog,
  ServiceCatalogCategory,
  ServiceCatalogCategoryServicesMap,
  ServiceCatalogSelections,
  ServiceCatalogSelectionsForAllTravelers,
  ServiceCatalogService,
  ServiceCatalogServices,
  ServiceCatalogTranslations,
  ServicesPerTraveler,
  ServicesState,
  ServicesStatus,
  TravelerService,
  CustomServiceType,
  ServiceSelections,
} from '@fcom/dapi/interfaces/services.interface';
import {
  entrySet,
  isEmpty,
  isEmptyObjectOrHasEmptyValues,
  isPresent,
  mapValues,
  valuesOf,
} from '@fcom/core/utils/utils';
import { PreciseNumber } from '@fcom/core/utils/precise-number';
import { Category } from '@fcom/dapi/api/models/category';
import { FinnairServiceCatalogCampaign } from '@fcom/dapi/api/models/finnair-service-catalog-campaign';
import { SubCategory } from '@fcom/dapi/api/models/sub-category';
import {
  PaxServiceCategories,
  PaxServiceCategory,
} from '@fcom/booking/modules/pax-details/interfaces/pax-ancillaries.interface';
import { cartStatusSelector } from '@fcom/booking/store/selectors/cart.selector';

import { CommonBookingFeatureState, QuantityForFragmentAndPassenger, CommonBookingState } from '../store.interface';
import {
  getOverlappingCategoriesForCategory,
  isBoundBasedCategory,
} from '../../modules/ancillaries/utils/category.utils';
import { VariantUnitsPerFragment } from '../../modules/ancillaries/utils/quota.utils';
import { selectCommonBookingState } from './feature.selector';
import {
  serviceCatalogFromCatalogSeatBundlesSelector,
  serviceCatalogSelector,
  serviceCategoriesFromCatalogSeatBundlesSelector,
  serviceCategoriesMapSelector,
  serviceCategoriesSelector,
} from './service-catalog.selector';
import { formatSelectionsByFragmentAndTraveler, removeEmptySelections } from '../../utils/service.utils';

const _servicesState = createSelector(selectCommonBookingState, (state: CommonBookingState) => state?.services);

export const serviceAvailabilitySelector = createSelector(
  _servicesState,
  (ss: ServicesState) => ss?.serviceAvailability
);

export const serviceCatalogCampaignsSelector = createDeepEqualSelector(
  serviceCatalogSelector,
  (catalog: ServiceCatalog) => catalog?.campaigns
);

const _campaignsShown = createSelector(_servicesState, (state: ServicesState) => state?.campaignsShown ?? []);
export const serviceSelections = createSelector(_servicesState, (ss: ServicesState) => ss?.selections);

const _servicesStatus = createSelector(_servicesState, (ss: ServicesState) => ss?.status);

const _servicesForCategory = (category: Category) =>
  createSelector(serviceCatalogSelector, (sc) =>
    sc ? sc.categories?.find((c) => c.category === category)?.services || {} : undefined
  );

/**
 * Maps each service category to its catalog translations (title, description, etc.). The sibling of
 * serviceCategoriesMapSelector (which maps categories to services), for consumers that need a category's
 * copy rather than its services.
 */
export const serviceCategoryTranslationsMapSelector: MemoizedSelector<
  CommonBookingFeatureState,
  Partial<Record<Category, ServiceCatalogTranslations>>
> = createSelector(serviceCategoriesSelector, (categories): Partial<Record<Category, ServiceCatalogTranslations>> =>
  (categories ?? []).reduce(
    (translationsByCategory, category) => {
      translationsByCategory[category.category] = category.translations;
      return translationsByCategory;
    },
    {} as Partial<Record<Category, ServiceCatalogTranslations>>
  )
);

/**
 * The catalog's service categories as plain category codes, in the catalog-defined display order. This is
 * the ordering authority for the shopping cart's purchasable product lines (see
 * `ShoppingCartBoundServicesComponent.productGroups`), mirroring how Travel Extras lists its categories. Seat
 * bundles are not (yet) part of the catalog, so consumers pin those separately.
 */
export const serviceCategoryOrderSelector: MemoizedSelector<CommonBookingFeatureState, Category[]> = createSelector(
  serviceCategoriesSelector,
  (categories): Category[] => (categories ?? []).map((category) => category.category)
);

export const servicesReadySelector = createSelector(
  cartStatusSelector,
  _servicesStatus,
  (cartStatus: CartStatus, servicesStatus: ServicesStatus) => {
    return cartStatus === CartStatus.OK && servicesStatus === ServicesStatus.OK;
  }
);

const _servicesForFragmentAndCategory = (fragmentId: string, category: Category) =>
  createSelector(_servicesForCategory(category), (ss: ServiceCatalogServices) =>
    ss ? ss[fragmentId] || {} : undefined
  );

export const serviceSelectionsForCategory = <T>(
  category: Category | SubCategory
): MemoizedSelector<CommonBookingFeatureState, ServiceCatalogSelections<T>> =>
  createSelector(_servicesState, (ss: ServicesState): ServiceCatalogSelections<T> => {
    if (!ss) {
      return undefined;
    }
    return ss.selections?.[category] ?? {};
  });

const _selectedServiceQuantitiesPerFragmentAndPax = (category: Category) =>
  createSelector(
    serviceSelectionsForCategory<TravelerService | TravelerService[]>(category),
    (selections): VariantUnitsPerFragment =>
      Object.entries(selections ?? {}).reduce<VariantUnitsPerFragment>(
        (unitsPerFragment, [fragmentId, selectionsPerTraveler]) => {
          Object.entries(selectionsPerTraveler ?? {}).forEach(([travelerId, selection]) => {
            []
              .concat(selection)
              .filter(isPresent)
              .filter(
                ({ includedInTicketType, includedInTierBenefit }) => !includedInTicketType && !includedInTierBenefit
              )
              .forEach(({ variant, quantity }) => {
                unitsPerFragment[fragmentId] = unitsPerFragment[fragmentId] ?? {};
                unitsPerFragment[fragmentId][travelerId] = unitsPerFragment[fragmentId][travelerId] ?? {};
                unitsPerFragment[fragmentId][travelerId][variant] =
                  (unitsPerFragment[fragmentId][travelerId][variant] ?? 0) + (quantity ?? 1);
              });
          });

          return unitsPerFragment;
        },
        {}
      )
  );

export const selectedServiceQuantitiesPerFragmentAndPaxSelector = (
  category: Category
): MemoizedSelector<CommonBookingFeatureState, VariantUnitsPerFragment> =>
  _selectedServiceQuantitiesPerFragmentAndPax(category);

const _variantsQuantity = (variants: string[]) =>
  createSelector(serviceSelections, (selections): QuantityForFragmentAndPassenger => {
    return Object.values(selections ?? {}).reduce(
      (allCategoriesQuantity, selection: ServiceCatalogSelections<TravelerService | TravelerService[]>) => {
        return entrySet(selection).reduce((quantitiesForFragment, service) => {
          return entrySet(service.value).reduce((innerQuantitiesForFragment, p) => {
            const services = [].concat(p.value);
            const newQuantity = services
              .filter(Boolean)
              .filter((s) => variants.includes(s.variant) && !s.includedInTicketType && !s.includedInTierBenefit)
              .reduce(
                (quantity, passengerService) => quantity + (passengerService.quantity ?? 1),
                innerQuantitiesForFragment[service.key]?.[p.key] ?? 0
              );

            if (!innerQuantitiesForFragment[service.key]) {
              innerQuantitiesForFragment[service.key] = {};
            }
            innerQuantitiesForFragment[service.key][p.key] = newQuantity;
            return innerQuantitiesForFragment;
          }, quantitiesForFragment);
        }, allCategoriesQuantity);
      },
      {}
    );
  });

const _upsell = createSelector(_servicesState, (ss: ServicesState): SelectedServiceData => ss?.upsell);

const _serviceSelectionsForFragmentAndCategory = <T>(fragmentId: string, category: Category) =>
  createSelector(serviceSelectionsForCategory(category), (ss: ServiceCatalogSelections<T>) =>
    ss ? ss[fragmentId] || {} : undefined
  );

const _allSelectionsForCategory = <T>(category: Category) =>
  createSelector(
    _servicesForCategory(category),
    serviceSelectionsForCategory<T>(category),
    (services, selections): [ServiceCatalogServices, ServiceCatalogSelections<T>] => {
      return [services, selections];
    }
  );

const _selectionsForCategories = (categories: Category[]) =>
  createSelector(serviceSelections, (selections): ServiceSelections =>
    categories.reduce((allCategories, category: Category) => {
      return {
        ...allCategories,
        [category]: selections?.[category] ?? {},
      };
    }, {})
  );

const _serviceMapForCategories = (categories: Category[]) =>
  createSelector(serviceCategoriesMapSelector, serviceCategoriesSelector, (categoriesMap, serviceCategories) => {
    const categoryMap = categories.reduce((allCategories, category: Category) => {
      const serviceCategory = serviceCategories?.find((sc) => sc.category === category);
      return isPresent(categoriesMap?.[category])
        ? [
            ...allCategories,
            {
              services: categoriesMap[category],
              translations: serviceCategory?.translations ?? {},
              media: serviceCategory?.media ?? {},
              category,
              isBoundBased: isBoundBasedCategory(category),
            },
          ]
        : allCategories;
    }, []);

    return isEmptyObjectOrHasEmptyValues(categoryMap) ? undefined : categoryMap;
  });

/**
 * Helps caching the overlaps for each category
 */
const overlapSelectorsByCategory = new Map<
  Category,
  MemoizedSelector<CommonBookingFeatureState, ServiceCatalogSelections<TravelerService[]>>
>();

export const overlappingSelectionsForCategorySelector = (
  category: Category
): MemoizedSelector<CommonBookingFeatureState, ServiceCatalogSelections<TravelerService[]>> => {
  if (!overlapSelectorsByCategory.has(category)) {
    overlapSelectorsByCategory.set(
      category,
      createSelector(_selectionsForCategories(getOverlappingCategoriesForCategory(category)), (selections) => {
        const nonEmptySelections = removeEmptySelections(selections);
        return formatSelectionsByFragmentAndTraveler(nonEmptySelections);
      })
    );
  }

  return overlapSelectorsByCategory.get(category)!;
};

export const serviceCategories = (
  preferCatalogSeatBundles = false
): StateSelector<CommonBookingFeatureState, ServiceCatalogCategory[]> =>
  safeSelect(preferCatalogSeatBundles ? serviceCategoriesFromCatalogSeatBundlesSelector : serviceCategoriesSelector);

export const serviceCategoriesMap = (): StateSelector<CommonBookingFeatureState, ServiceCatalogCategoryServicesMap> => {
  return safeSelect(serviceCategoriesMapSelector);
};

// Seatmap-sourced seat bundles are the default; cart-catalog sourcing stays a no-op until opted into via
// /devtool → Bundles, hence preferCatalogSeatBundles defaults to false.
export const serviceCatalog = (
  preferCatalogSeatBundles = false
): StateSelector<CommonBookingFeatureState, ServiceCatalog> =>
  select(preferCatalogSeatBundles ? serviceCatalogFromCatalogSeatBundlesSelector : serviceCatalogSelector);

export const serviceAvailability = (): StateSelector<CommonBookingFeatureState, ServiceAvailability> =>
  safeSelect(serviceAvailabilitySelector);

export const serviceCatalogCampaigns = (): StateSelector<CommonBookingFeatureState, FinnairServiceCatalogCampaign[]> =>
  safeSelect(serviceCatalogCampaignsSelector);

export const serviceCatalogCampaignIdsByFragmentSelector = createSelector(
  serviceCatalogCampaignsSelector,
  (campaigns): Record<string, Record<Category, string[]>> =>
    (campaigns ?? []).reduce(
      (campaignIdsByFragment, campaign) => {
        campaign.categories.forEach((campaignCategory) => {
          Object.keys(campaignCategory.fragments).forEach((fragmentId) => {
            const campaignIdsByCategory = (campaignIdsByFragment[fragmentId] ??= {} as Record<Category, string[]>);
            campaignIdsByCategory[campaignCategory.id] = [
              ...(campaignIdsByCategory[campaignCategory.id] ?? []),
              campaign.id,
            ];
          });
        });
        return campaignIdsByFragment;
      },
      {} as Record<string, Record<Category, string[]>>
    )
);
export const campaignsShown = (): StateSelector<CommonBookingFeatureState, string[]> => safeSelect(_campaignsShown);

export const serviceSelectionsForFragment = <T>(
  fragmentId: string,
  category: Category
): StateSelector<CommonBookingFeatureState, SelectionsPerTraveler<T>> =>
  safeSelect(_serviceSelectionsForFragmentAndCategory<T>(fragmentId, category));

export const variantsQuantity = (
  variants: string[]
): StateSelector<CommonBookingFeatureState, QuantityForFragmentAndPassenger> => {
  return select(_variantsQuantity(variants));
};

export const serviceSelectionsForFragmentAndTraveler = <T>(
  fragmentId: string,
  travelerId: string,
  category: Category
): MemoizedSelector<CommonBookingFeatureState, T> =>
  createSelector(
    _serviceSelectionsForFragmentAndCategory(fragmentId, category),
    (selection: SelectionsPerTraveler<T>): T => selection?.[travelerId]
  );

export const servicesState = (): StateSelector<CommonBookingFeatureState, ServicesState> => select(_servicesState);
export const servicesStatus = (): StateSelector<CommonBookingFeatureState, ServicesStatus> => select(_servicesStatus);
export const servicesReady = (): StateSelector<CommonBookingFeatureState, boolean> => select(servicesReadySelector);
export const upsell = (): StateSelector<CommonBookingFeatureState, SelectedServiceData> => select(_upsell);

export const servicesForCategory = (
  category: Category
): StateSelector<CommonBookingFeatureState, ServiceCatalogServices> => safeSelect(_servicesForCategory(category));

export const servicesForFragmentAndCategory = (
  fragmentId: string,
  category: Category
): StateSelector<CommonBookingFeatureState, ServicesPerTraveler> =>
  safeSelect(_servicesForFragmentAndCategory(fragmentId, category));

export const serviceMapForCategories = (
  categories: Category[]
): StateSelector<CommonBookingFeatureState, ServiceCatalogCategory[]> =>
  safeSelect(_serviceMapForCategories(categories));

export const serviceSelectionsForCategoryAllTravelersSelector = (
  category: Category
): MemoizedSelector<CommonBookingFeatureState, ServiceCatalogSelectionsForAllTravelers> =>
  createSelector(
    _allSelectionsForCategory<TravelerService | TravelerService[]>(category),
    ([services, selections]): ServiceCatalogSelectionsForAllTravelers => {
      return entrySet(selections).reduce((all, selection) => {
        all[selection.key] = entrySet(selection.value).reduce<TravelerService[]>(
          (allServices: TravelerService[], traveler) => {
            const servicesForTraveler: ServiceCatalogService[] = services?.[selection.key]?.[traveler.key] || [];
            const travelerServices = Array.isArray(traveler.value) ? traveler.value : [traveler.value];
            const translatedServices = travelerServices
              .filter(Boolean)
              .map(addTranslationsToService(servicesForTraveler));
            translatedServices.forEach((service) => (service.travelerId = traveler.key));
            return allServices.concat(translatedServices);
          },
          []
        );
        return all;
      }, {});
    }
  );

export const serviceMapForCategoriesForTravelers = (
  travelerIds: string[],
  categories: Category[]
): StateSelector<CommonBookingFeatureState, PaxServiceCategories> =>
  pipe(
    safeSelect(_serviceMapForCategories(categories)),
    map((serviceCategories: PaxServiceCategory[]) => {
      return travelerIds.reduce((serviceMapForTravelers, travelerId) => {
        return {
          ...serviceMapForTravelers,
          [travelerId]: serviceCategories.map((category) => ({
            ...category,
            services: mapValues(category.services, (fragment, fragmentId) => {
              const travelerOrGroup = fragmentId === CustomServiceType.JOURNEY ? CustomServiceType.GROUP : travelerId;
              // Filter out unwanted service variants and subCategories
              return fragment?.[travelerOrGroup]
                ?.filter((service) => ![...NO_VARIANTS, '_NOLOUNGE'].includes(service.variant))
                .filter((service) => ![SubCategory.HEAVY_OR_LARGE].includes(service.subCategory));
            }),
          })),
        };
      }, {});
    })
  );

export const selectionMapForCategoriesForTravelers = (
  travelerIds: string[],
  categories: Category[]
): StateSelector<CommonBookingFeatureState, ServiceSelections> =>
  pipe(
    safeSelect(_selectionsForCategories(categories)),
    map((selections) => {
      return travelerIds.reduce(
        (selectionsForTravelers, travelerId) => ({
          ...selectionsForTravelers,
          [travelerId]: mapValues(selections, (category) =>
            mapValues(category, (fragment, fragmentId) => {
              const travelerOrGroup = fragmentId === CustomServiceType.JOURNEY ? CustomServiceType.GROUP : travelerId;
              return fragment?.[travelerOrGroup];
            })
          ),
        }),
        {}
      );
    })
  );

/**
 * Returns the current non-empty selections of every category that overlaps the given one.
 * E.g. WIFI_MEAL => WIFI, MEAL, LOUNGE_WIFI, SEAT_MEAL, SEAT_WIFI (only those that actually have selections).
 */
export const overlappingSelectionsForCategory = (
  category: Category
): StateSelector<CommonBookingFeatureState, ServiceCatalogSelections<TravelerService[]>> =>
  pipe(select(overlappingSelectionsForCategorySelector(category)));

export const totalPriceForSelectionsForCategories = (
  categories: Category[]
): StateSelector<CommonBookingFeatureState, Amount> =>
  pipe(
    safeSelect(_selectionsForCategories(categories)),
    map((selections: ServiceSelections): Array<Amount & { quantity: number }> =>
      valuesOf(selections as Record<string, ServiceCatalogSelections<TravelerService | TravelerService[]>>)
        .flatMap((category) =>
          valuesOf(category).flatMap((fragment) =>
            valuesOf(fragment).flatMap((travelerSelection) => {
              if (Array.isArray(travelerSelection)) {
                return travelerSelection?.map((selection) => ({
                  ...selection.pricePerItem,
                  quantity: selection.quantity,
                }));
              }
              return isPresent(travelerSelection)
                ? { ...travelerSelection.pricePerItem, quantity: travelerSelection.quantity }
                : undefined;
            })
          )
        )
        .filter(Boolean)
    ),
    map((allPrices: Array<Amount & { quantity: number }>): Amount =>
      !isEmpty(allPrices)
        ? allPrices.reduce(
            (totalPrice, selectionPrice) => {
              const selectionSubTotal = PreciseNumber.fromString(selectionPrice.amount).multiply(
                PreciseNumber.from(selectionPrice.quantity, 0)
              );

              return {
                currencyCode: totalPrice.currencyCode || selectionPrice.currencyCode,
                amount: PreciseNumber.fromString(totalPrice.amount).add(selectionSubTotal).toString(),
              };
            },
            {
              currencyCode: null,
              amount: '0',
            }
          )
        : undefined
    )
  );

function addTranslationsToService(servicesForTraveler: ServiceCatalogService[]) {
  return (travelerService): TravelerService => {
    const serviceForTraveler = servicesForTraveler.find((s) => s.variant === travelerService.variant);
    if (serviceForTraveler) {
      return {
        ...travelerService,
        ...(travelerService.category === Category.SEAT ? { seatNumber: travelerService.variant } : {}),
        title: serviceForTraveler.translations?.title,
        maxBaggageWeight: serviceForTraveler.parameters?.baggageWeight,
      };
    }
    return travelerService;
  };
}
